// NOTE: NSAI-7841 — the two slips a model makes in an otherwise valid ```ui
// block that are safe to absorb instead of rejecting the whole interface.
//
// Free text past its budget is kept, not refused: QA saw comparison verdicts
// of 141-177 characters against a 140 budget, and a rejected block shows the
// user raw JSON — far worse than a long verdict. The renderers show the first
// 140 characters with "…" (`readUiDisplayText`) and the whole text behind an
// info icon, so nothing the model wrote is lost. Labels and titles are not
// shortened: they name things, and half a name misleads.
//
// A number written as text ("1") is read as the number: models write a plot's
// `step` beside `from` / `to`, which are text formulas, and copy their quotes.

import {
  MAX_UI_TEXT_LENGTH,
  UI_JOINING_CODE_POINT_RANGES,
  UI_NUMERAL_PATTERN,
  UI_REGIONAL_INDICATOR_RANGE,
  UI_TEXT_ELLIPSIS,
  UI_ZERO_WIDTH_JOINER,
} from "../../constants/ui";
import type { IShortenUiTextProps, IUiDisplayText } from "../../types/ui";

const isJoiningCodePoint = (codePoint: number): boolean =>
  UI_JOINING_CODE_POINT_RANGES.some(
    ([first, last]) => codePoint >= first && codePoint <= last,
  );

const isRegionalIndicator = (character: string): boolean => {
  const codePoint = character.codePointAt(0) ?? 0;

  return (
    [...character].length === 1 &&
    codePoint >= UI_REGIONAL_INDICATOR_RANGE[0] &&
    codePoint <= UI_REGIONAL_INDICATOR_RANGE[1]
  );
};

// NOTE: The text split into the characters a reader sees, so a cut never
// lands inside an emoji sequence or a flag. A small rule rather than
// Intl.Segmenter, which the mobile engine (Hermes) does not reliably offer,
// so web and mobile cut at the same place.
const splitDisplayedCharacters = (text: string): string[] => {
  const characters: string[] = [];

  for (const codePoint of text) {
    const last = characters.at(-1);
    const isFlagHalf =
      last !== undefined &&
      isRegionalIndicator(last) &&
      isRegionalIndicator(codePoint);
    const isJoiningLast =
      last !== undefined &&
      (isJoiningCodePoint(codePoint.codePointAt(0) ?? 0) ||
        last.endsWith(UI_ZERO_WIDTH_JOINER) ||
        isFlagHalf);

    if (isJoiningLast) {
      characters[characters.length - 1] = `${last}${codePoint}`;
    } else {
      characters.push(codePoint);
    }
  }

  return characters;
};

export const shortenUiText = ({
  text,
  maxLength,
}: IShortenUiTextProps): string => {
  if (text.length <= maxLength) {
    return text;
  }

  // NOTE: Budgets count UTF-16 units, like zod's `.max`; whole displayed
  // characters are kept until the next one would not fit with the ellipsis.
  const budget = maxLength - UI_TEXT_ELLIPSIS.length;
  let kept = "";

  for (const character of splitDisplayedCharacters(text)) {
    if (kept.length + character.length > budget) {
      break;
    }

    kept += character;
  }

  return `${kept.trimEnd()}${UI_TEXT_ELLIPSIS}`;
};

// NOTE: What a renderer shows for a free-text field (a verdict, a hint, a
// step's label or detail), and whether to offer the full text beside it.
export const readUiDisplayText = (fullText: string): IUiDisplayText => {
  const text = shortenUiText({ text: fullText, maxLength: MAX_UI_TEXT_LENGTH });

  return { text, fullText, isShortened: text !== fullText };
};

export const readUiNumeral = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();

  return UI_NUMERAL_PATTERN.test(trimmed) ? Number(trimmed) : value;
};
